/**
 * Frequently Bought Together — Auto Rules Engine
 * assets/frequently-bought-together.js
 *
 * SETUP:
 *  1. Edit FBT_RULES below to match your product titles / buckets
 *  2. Upload this file to assets/ — no other config needed
 *
 * HOW IT WORKS:
 *  - Reads data-product-title from the section element
 *  - Detects product family (5-in-1, Power Pellets, Mylar) + bucket (F&C, Lav, LF, Ath)
 *  - Looks up the rules table → gets suggestion search terms
 *  - Fetches matching products from Shopify via /search/suggest.json
 *  - Renders product cards dynamically — no manual PDP setup needed
 */

(function () {
  'use strict';

  // ═══════════════════════════════════════════════════════════
  //  RULES TABLE
  //  Each rule: { match, bucket, suggestions[] }
  //
  //  match       — string that the product title must CONTAIN (case-insensitive)
  //  bucket      — string the title must ALSO contain to apply this rule
  //                use '' to match any bucket for that product family
  //  suggestions — array of search terms used to find each suggested product
  //                (matches against product titles in your store)
  //                Add or remove entries to show 1, 2 or 3 suggestions
  // ═══════════════════════════════════════════════════════════
  const FBT_RULES = [
    // ── 5-in-1 ──────────────────────────────────────────────
    { match: '5-in-1', bucket: 'free and clear', suggestions: ['linen fresh',  'free and clear'] },
    { match: '5-in-1', bucket: 'lavender', suggestions: ['linen fresh',  'free and clear'] },
    { match: '5-in-1', bucket: 'linen fresh',  suggestions: ['free and clear', 'athletic'] },
    { match: '5-in-1', bucket: 'athletic', suggestions: ['linen fresh',  'free and clear'] },

    { match: 'CoinVend', bucket: 'free and clear', suggestions: ['CoinVend linen fresh',  'CoinVend lavender'] },
    { match: 'CoinVend', bucket: 'lavender',        suggestions: ['CoinVend linen fresh',  'CoinVend free and clear'] },
    { match: 'CoinVend', bucket: 'linen fresh',     suggestions: ['CoinVend free and clear', 'CoinVend lavender'] },
    { match: 'CoinVend', bucket: 'athletic',         suggestions: ['CoinVend linen fresh',  'CoinVend free and clear'] },

    // ── Power Pellets ────────────────────────────────────────
    { match: 'Power Pellets', bucket: 'free and clear', suggestions: ['5-in-1 linen fresh'] },
    { match: 'Power Pellets', bucket: 'lavender', suggestions: ['5-in-1 linen fresh'] },
    { match: 'Power Pellets', bucket: 'linen fresh',  suggestions: ['Free & Clear'] },

    // ── Mylar ────────────────────────────────────────────────
    { match: 'Mylar', bucket: 'free and clear', suggestions: ['linen fresh'] },
    { match: 'Mylar', bucket: 'lavender', suggestions: ['linen fresh'] },
    { match: 'Mylar', bucket: 'linen fresh',  suggestions: ['free and clear'] },
    { match: 'Mylar', bucket: 'athletic', suggestions: ['linen fresh'] },

    {
      match: '5-in-1',
      bucket: 'citrus',
      suggestions: ['linen fresh', 'free and clear']
    },
  ];

// ═══════════════════════════════════════════════════════════
  //  BUCKET KEYWORDS
  //  Scent/variant names as they appear in your product titles.
  //  Order matters — put longer/more specific phrases first.
  // ═══════════════════════════════════════════════════════════
  const BUCKET_KEYWORDS = ['free and clear', 'linen fresh', 'lavender', 'athletic'];
 
  // ── DOM refs ──────────────────────────────────────────────
  const section = document.querySelector('[data-section-type="frequently-bought-together"]');
  if (!section) return;
 
  const container   = section.querySelector('#fbt-container');
  const ctaRow      = section.querySelector('#fbt-cta-row');
  const totalEl     = section.querySelector('#fbt-total-price');
  const addBtn      = section.querySelector('#fbt-add-to-cart');
  const noteEl      = section.querySelector('#fbt-note');
 
  // ── Read data from the section element ───────────────────
  const productTitle    = section.dataset.productTitle  || '';
  const variantId       = section.dataset.variantId;
  const variantPrice    = parseInt(section.dataset.variantPrice, 10) || 0;
  const productImage    = section.dataset.productImage  || '';
  const productUrl      = section.dataset.productUrl    || '';
  const noteText        = section.dataset.note          || '';
 
  // ── State ─────────────────────────────────────────────────
  // items = [{ variantId, price, title, checked }]
  let items = [];
 
  // ─────────────────────────────────────────────────────────
  //  STEP 1 — Match rule from product title
  // ─────────────────────────────────────────────────────────
  function detectBucket(title) {
    const t = title.toLowerCase();

    // Free & Clear variations
    if (
      t.includes('free and clear') ||
      t.includes('free & clear') ||
      t.includes('fragrance-free')
    ) {
      return 'free and clear';
    }

    // Linen Fresh
    if (t.includes('linen fresh')) {
      return 'linen fresh';
    }

    // Lavender
    if (t.includes('lavender')) {
      return 'lavender';
    }

    // Athletic variations
    if (
      t.includes('athletic') ||
      t.includes('athletics') ||
      t.includes('sports gear') ||
      t.includes('activewear')
    ) {
      return 'athletic';
    }

    if (t.includes('citrus')) {
      return 'citrus';
    }

    return '';
  }
 
  function matchRule(title) {
    const t      = title.toLowerCase();
    const bucket = detectBucket(title);
 
    for (const rule of FBT_RULES) {
      const matchOk  = t.includes(rule.match.toLowerCase());
      const bucketOk = rule.bucket === '' || bucket.toUpperCase() === rule.bucket.toUpperCase();
      if (matchOk && bucketOk) return rule;
    }
    return null;
  }
 
  // ─────────────────────────────────────────────────────────
  //  STEP 2 — Fetch product by search term
  // ─────────────────────────────────────────────────────────
  async function fetchProductByQuery(query) {
    try {
      // Fetch up to 5 results so we can skip the current product if it appears
      const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=5`;
      const res = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!res.ok) return null;
      const data = await res.json();
      const results = data?.resources?.results?.products;
      if (!results || !results.length) return null;
 
      // Skip the current product so we never suggest the item already on the page
      const currentHandle = section.dataset.productHandle || '';
      const normalizedQuery = query.toLowerCase();

      const p =
        results.find(r =>
          r.handle !== currentHandle &&
          r.title.toLowerCase().includes(normalizedQuery)
        ) || results.find(r => r.handle !== currentHandle) || results[0];
 
      // Fetch full product JSON for variant ID + price
      const pRes = await fetch(`/products/${p.handle}.js`);
      if (!pRes.ok) return null;
      const full = await pRes.json();
      const variant = full.variants?.[0];
      if (!variant) return null;
 
      return {
        variantId: String(variant.id),
        price:     variant.price,
        title:     full.title,
        url:       `/products/${full.handle}`,
        image:     full.featured_image || p.image?.url || '',
      };
    } catch (_) {
      return null;
    }
  }
 
  // ─────────────────────────────────────────────────────────
  //  STEP 3 — Render cards
  // ─────────────────────────────────────────────────────────
  function formatMoney(cents) {
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
    }
    return '$' + (cents / 100).toFixed(2);
  }
 
  function buildPrimaryCard() {
    return `
      <div class="fbt-item fbt-item--primary" data-variant-id="${variantId}" data-price="${variantPrice}" data-checked="true">
        <div class="fbt-item__checkbox fbt-item__checkbox--fixed">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <polyline points="2,6 5,9 10,3" stroke="white" stroke-width="1.8" stroke-linecap="round"/>
          </svg>
        </div>
        <a href="${productUrl}" class="fbt-item__image-wrap">
          ${productImage
            ? `<img src="${productImage}" alt="" class="fbt-item__image" loading="lazy">`
            : `<div class="fbt-item__image fbt-item__image--placeholder"></div>`
          }
          <span class="fbt-item__badge">This item</span>
        </a>
        <div class="fbt-item__info">
          <p class="fbt-item__label">This item</p>
          <p class="fbt-item__title">${productTitle}</p>
          <p class="fbt-item__price">${formatMoney(variantPrice)}</p>
        </div>
      </div>`;
  }
 
  function buildBundleCard(product, index) {
    return `
      <div class="fbt-separator"><span class="fbt-separator__plus">+</span></div>
      <div class="fbt-item fbt-item--bundle"
           data-variant-id="${product.variantId}"
           data-price="${product.price}"
           data-checked="true">
        <div class="fbt-item__checkbox fbt-item__checkbox--toggle fbt-item__checkbox--on" role="checkbox" aria-checked="true" tabindex="0">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <polyline points="2,6 5,9 10,3" stroke="white" stroke-width="1.8" stroke-linecap="round"/>
          </svg>
        </div>
        <a href="${product.url}" class="fbt-item__image-wrap">
          ${product.image
            ? `<img src="${product.image}" alt="" class="fbt-item__image" loading="lazy">`
            : `<div class="fbt-item__image fbt-item__image--placeholder"></div>`
          }
        </a>
        <div class="fbt-item__info">
          <p class="fbt-item__label">Add-on ${index}</p>
          <p class="fbt-item__title">${product.title}</p>
          <p class="fbt-item__price">${formatMoney(product.price)}</p>
        </div>
      </div>`;
  }
 
  function renderCards(bundleProducts) {
    let html = buildPrimaryCard();
    bundleProducts.forEach((p, i) => { html += buildBundleCard(p, i + 1); });
    container.innerHTML = html;
 
    // Build state
    items = [
      { variantId, price: variantPrice, checked: true },
      ...bundleProducts.map(p => ({ variantId: p.variantId, price: p.price, checked: true })),
    ];
 
    // Attach toggle listeners
    container.querySelectorAll('.fbt-item__checkbox--toggle').forEach((cb, i) => {
      cb.addEventListener('click',   () => toggleItem(i + 1, cb));
      cb.addEventListener('keydown', e => { if (e.key === ' ' || e.key === 'Enter') toggleItem(i + 1, cb); });
    });
 
    updateTotal();
    ctaRow.style.display  = '';
    noteEl.textContent    = noteText;
    noteEl.style.display  = '';
    section.style.display = '';
  }
 
  // ─────────────────────────────────────────────────────────
  //  STEP 4 — Toggle + total
  // ─────────────────────────────────────────────────────────
  function toggleItem(index, cbEl) {
    items[index].checked = !items[index].checked;
    const card = cbEl.closest('.fbt-item');
    if (items[index].checked) {
      cbEl.classList.add('fbt-item__checkbox--on');
      cbEl.setAttribute('aria-checked', 'true');
      card.style.opacity = '1';
    } else {
      cbEl.classList.remove('fbt-item__checkbox--on');
      cbEl.setAttribute('aria-checked', 'false');
      card.style.opacity = '0.42';
    }
    updateTotal();
  }
 
  function updateTotal() {
    const checked = items.filter(i => i.checked);
    const total   = checked.reduce((s, i) => s + i.price, 0);
    totalEl.textContent = formatMoney(total);
    totalEl.style.transform = 'scale(1.07)';
    totalEl.style.color     = '#1d4ed8';
    setTimeout(() => { totalEl.style.transform = ''; totalEl.style.color = ''; }, 260);
 
    const count = checked.length;
    addBtn.disabled = count === 0;
    addBtn.innerHTML = `
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
        <path d="M1 1h4l2.68 13.39a2 2 0 001.99 1.61h9.72a2 2 0 001.99-1.61L23 6H6"/>
      </svg>
      ${count > 1 ? `Add ${count} Items to Cart` : 'Add to Cart'}`;
  }
 
  // ─────────────────────────────────────────────────────────
  //  STEP 5 — Add to cart + open drawer
  // ─────────────────────────────────────────────────────────
  async function fetchCart() {
    try {
      const r = await fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      return r.ok ? r.json() : null;
    } catch (_) { return null; }
  }
 
  // ── Detect the cart drawer element + its Shopify section id ──
  function findCartDrawerEl() {
    // Common selectors across themes
    const selectors = [
      '#cart-drawer',
      '#CartDrawer',
      '#cart-notification',
      '[id*="cart-drawer"]',
      '[id*="CartDrawer"]',
      'cart-drawer',           // custom element (Dawn)
      'cart-notification',
    ];
    for (const s of selectors) {
      const el = document.querySelector(s);
      if (el) return el;
    }
    return null;
  }
 
  function getCartSectionId() {
    // Try to read the section id from the drawer element itself
    const el = findCartDrawerEl();
    if (el) {
      // Shopify themes stamp data-id or id on the section wrapper
      if (el.dataset.id)   return el.dataset.id;
      if (el.id)           return el.id;           // e.g. "cart-drawer"
    }
    // Common fallback names used by popular themes
    return 'cart-drawer';
  }
 
  async function refreshCartSection() {
    try {
      // Your theme fetches these two sections to re-render the drawer
      const r = await fetch('/?sections=cart-drawer,cart-counter', {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!r.ok) return;
      const data = await r.json();
 
      // Update the drawer body (exact selector from your theme's cart.js)
      const drawerBody = document.querySelector('#CartDrawer-Body');
      if (drawerBody && data['cart-drawer']) {
        const parsed = new DOMParser()
          .parseFromString(data['cart-drawer'], 'text/html')
          .querySelector('#shopify-section-cart-drawer #CartDrawer-Body');
        if (parsed) drawerBody.innerHTML = parsed.innerHTML;
      }
 
      // Update the cart counter badge
      const counter = document.querySelector('#cart-counter');
      if (counter && data['cart-counter']) {
        const parsed = new DOMParser()
          .parseFromString(data['cart-counter'], 'text/html')
          .querySelector('#shopify-section-cart-counter');
        if (parsed) counter.innerHTML = parsed.innerHTML;
      }
    } catch (_) {}
  }
 
  function fireRefreshEvents(cart) {
    const detail = { cart, bubbles: true };
    [
      'cart:refresh', 'cart:updated', 'cart:change',
      'theme:cart:refresh', 'theme:cart:updated',
      'ajaxProduct:added',
    ].forEach(name => {
      document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: { cart } }));
      window.dispatchEvent(new CustomEvent(name,   { bubbles: true, detail: { cart } }));
    });
    if (typeof window.CartJS?.getCart === 'function') window.CartJS.getCart();
  }
 
  function fireOpenEvents() {
    // Your theme uses a <cart-drawer> custom element with an open() method.
    // It also opens via [data-cart-link] click. Both are tried below.
 
    // ── Method 1: direct custom element API (most reliable) ──
    const cartDrawerEl = document.querySelector('cart-drawer');
    if (cartDrawerEl && typeof cartDrawerEl.open === 'function') {
      cartDrawerEl.open();
      return;
    }
 
    // ── Method 2: click [data-cart-link] (theme's own trigger) ──
    const cartLink = document.querySelector('[data-cart-link]');
    if (cartLink) {
      cartLink.click();
      return;
    }
 
    // ── Method 3: add is-visible class directly (fallback) ──
    const drawerEl = document.querySelector('cart-drawer');
    if (drawerEl) drawerEl.classList.add('is-visible');
  }
 
  addBtn.addEventListener('click', async function () {
    const checked = items.filter(i => i.checked);
    if (!checked.length) return;
 
    const originalHTML = addBtn.innerHTML;
    addBtn.disabled    = true;
    addBtn.innerHTML   = '<span class="fbt-spinner"></span> Adding…';
 
    try {
      // ── 1. POST to cart ────────────────────────────────────
      const res = await fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body:    JSON.stringify({
          items: checked.map(i => ({ id: parseInt(i.variantId, 10), quantity: 1 })),
        }),
      });
      if (!res.ok) throw new Error('cart error');
 
      addBtn.innerHTML        = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20,6 9,17 4,12"/></svg> Added!`;
      addBtn.style.background = '#16a34a';
 
      // ── 2. Get latest cart JSON ────────────────────────────
      const cart = await fetchCart();
 
      // ── 3. Fire refresh events + update drawer HTML ────────
      fireRefreshEvents(cart);
      await refreshCartSection();
 
      // ── 4. Small pause → then open ────────────────────────
      await new Promise(r => setTimeout(r, 350));
      fireOpenEvents();
 
      // ── 5. Safety retry: open again after 700 ms in case
      //       the theme needs time to finish re-rendering ─────
      setTimeout(() => fireOpenEvents(), 700);
 
      setTimeout(() => {
        addBtn.innerHTML        = originalHTML;
        addBtn.style.background = '';
        addBtn.disabled         = false;
      }, 2800);
 
    } catch (_) {
      addBtn.innerHTML        = '⚠ Error — try again';
      addBtn.style.background = '#dc2626';
      setTimeout(() => {
        addBtn.innerHTML        = originalHTML;
        addBtn.style.background = '';
        addBtn.disabled         = false;
      }, 2800);
    }
  });
 
  // ─────────────────────────────────────────────────────────
  //  INIT — Match rule → fetch products → render
  // ─────────────────────────────────────────────────────────
  async function init() {
    const rule = matchRule(productTitle);
    if (!rule || !rule.suggestions.length) {
      // No rule matched — hide the section entirely
      section.style.display = 'none';
      return;
    }
 
    // Fetch all suggested products in parallel
    const fetched = await Promise.all(rule.suggestions.map(fetchProductByQuery));
    const valid   = fetched.filter(Boolean);
 
    if (!valid.length) {
      section.style.display = 'none';
      return;
    }
 
    renderCards(valid);
  }
 
  init();
 
})();